/**
 * Body Batteryゲージ
 *
 * Garmin Body Batteryを視覚的に表示（0-100のゲージ）
 */

import { Battery, BatteryCharging, BatteryLow, BatteryMedium } from 'lucide-react'

import { cn } from '@/lib/utils'

type Props = {
  current: number | null
  highest: number | null
  lowest: number | null
  charged: number | null
  drained: number | null
  className?: string
}

const getBatteryIcon = (value: number | null) => {
  if (value === null) return <Battery className="size-5" />
  if (value >= 70) return <BatteryCharging className="size-5 text-green-500" />
  if (value >= 30) return <BatteryMedium className="size-5 text-yellow-500" />
  return <BatteryLow className="size-5 text-red-500" />
}

const getBatteryColor = (value: number | null) => {
  if (value === null) return 'bg-muted'
  if (value >= 70) return 'bg-green-500'
  if (value >= 30) return 'bg-yellow-500'
  return 'bg-red-500'
}

export const BodyBatteryGauge = ({ current, highest, lowest, charged, drained, className }: Props) => {
  const percentage = current ?? 0

  return (
    <div className={cn('rounded-xl border bg-card p-4 shadow-sm', className)}>
      <div className="flex items-center gap-2 mb-3">
        {getBatteryIcon(current)}
        <span className="text-sm font-medium">Body Battery</span>
      </div>

      {/* メインゲージ */}
      <div className="relative h-6 bg-muted rounded-full overflow-hidden mb-2">
        <div
          className={cn('h-full transition-all duration-500 rounded-full', getBatteryColor(current))}
          style={{ width: `${percentage}%` }}
        />
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-sm font-bold text-foreground drop-shadow-sm">{current !== null ? current : '--'}</span>
        </div>
      </div>

      {/* 詳細情報 */}
      <div className="grid grid-cols-2 gap-2 text-xs">
        <div className="flex items-center justify-between p-2 bg-muted/50 rounded-lg">
          <span className="text-muted-foreground">最高</span>
          <span className="font-medium text-green-600">{highest ?? '--'}</span>
        </div>
        <div className="flex items-center justify-between p-2 bg-muted/50 rounded-lg">
          <span className="text-muted-foreground">最低</span>
          <span className="font-medium text-red-600">{lowest ?? '--'}</span>
        </div>
        <div className="flex items-center justify-between p-2 bg-muted/50 rounded-lg">
          <span className="text-muted-foreground">充電</span>
          <span className="font-medium text-blue-600">+{charged ?? '--'}</span>
        </div>
        <div className="flex items-center justify-between p-2 bg-muted/50 rounded-lg">
          <span className="text-muted-foreground">消費</span>
          <span className="font-medium text-orange-600">-{drained ?? '--'}</span>
        </div>
      </div>
    </div>
  )
}
